import {
  isInternalTarget,
  type C4ComponentDefinition,
  type C4ComponentTarget,
} from './c4Components';

export const CARD_WIDTH = 240;
export const CARD_PADDING = 12;
export const LINE_HEIGHT = 14;
export const NAME_FONT_SIZE = 13;
export const TEXT_FONT_SIZE = 10.5;
export const GAP_X = 40;
export const GAP_Y = 64;
export const CANVAS_PADDING = 44;
export const CHANNEL_WIDTH = 24;
export const CHANNEL_LANE_WIDTH = 16;
export const BOUNDARY_PADDING = 16;
const MAX_DESCRIPTION_LINES = 4;
const CHAR_WIDTH = 5.4;

export type C4NodeType = 'internal' | 'external';

export interface C4LayoutNode {
  id: string;
  type: C4NodeType;
  title: string;
  subtitle?: string;
  descriptionLines: string[];
  entityRef?: string;
  rank: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface C4LayoutEdge {
  from: string;
  to: string;
  path: string;
}

export interface C4Layout {
  nodes: C4LayoutNode[];
  edges: C4LayoutEdge[];
  width: number;
  height: number;
}

function key(value: string): string {
  return value.trim().toLocaleLowerCase('en-US');
}

export function wrapText(
  text: string,
  width: number,
  maxLines = MAX_DESCRIPTION_LINES,
): string[] {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return [];
  }

  const perLine = Math.max(8, Math.floor(width / CHAR_WIDTH));
  const lines: string[] = [];
  let current = '';

  for (const word of normalized.split(' ')) {
    if (!current) {
      current = word;
    } else if (current.length + 1 + word.length <= perLine) {
      current = `${current} ${word}`;
    } else {
      lines.push(current);
      current = word;
      if (lines.length === maxLines) {
        break;
      }
    }
  }

  if (lines.length < maxLines && current) {
    lines.push(current);
  }

  if (lines.length === maxLines) {
    const last = lines[maxLines - 1];
    const remaining = normalized.slice(lines.join(' ').length).trim();
    if (remaining) {
      lines[maxLines - 1] = `${last.slice(0, Math.max(1, perLine - 1))}…`;
    }
  }

  return lines;
}

function nodeHeight(node: {
  subtitle?: string;
  descriptionLines: string[];
}): number {
  const lines = 1 + (node.subtitle ? 1 : 0) + node.descriptionLines.length;
  return CARD_PADDING * 2 + lines * LINE_HEIGHT + 4;
}

export function shortRefName(ref: string): string {
  const afterKind = ref.includes(':') ? ref.slice(ref.indexOf(':') + 1) : ref;
  return afterKind.includes('/')
    ? afterKind.slice(afterKind.lastIndexOf('/') + 1)
    : afterKind;
}

export function refKindLabel(ref: string): string {
  const kind = ref.includes(':') ? ref.slice(0, ref.indexOf(':')) : '';
  if (!kind) {
    return 'Entidade';
  }
  return kind.charAt(0).toLocaleUpperCase('en-US') + kind.slice(1);
}

interface RawNode {
  id: string;
  type: C4NodeType;
  title: string;
  subtitle?: string;
  descriptionLines: string[];
  entityRef?: string;
}

function externalId(target: C4ComponentTarget): string {
  return target.entityRef ?? key(target.raw);
}

function collectNodes(components: C4ComponentDefinition[]): RawNode[] {
  const nodes: RawNode[] = components.map(component => ({
    id: key(component.name),
    type: 'internal',
    title: component.name,
    ...(component.technology
      ? { subtitle: `[Component: ${component.technology}]` }
      : {}),
    descriptionLines: component.description
      ? wrapText(component.description, CARD_WIDTH - CARD_PADDING * 2)
      : [],
  }));

  const seen = new Set(nodes.map(node => node.id));
  for (const component of components) {
    for (const target of component.targets) {
      if (isInternalTarget(target, components)) {
        continue;
      }
      const id = externalId(target);
      if (seen.has(id)) {
        continue;
      }
      seen.add(id);
      nodes.push({
        id,
        type: 'external',
        title: target.entityRef ? shortRefName(target.entityRef) : target.raw,
        subtitle: target.entityRef
          ? `[${refKindLabel(target.entityRef)}]`
          : '[Externo]',
        descriptionLines: [],
        ...(target.entityRef ? { entityRef: target.entityRef } : {}),
      });
    }
  }

  return nodes;
}

function collectEdges(
  components: C4ComponentDefinition[],
  nodes: RawNode[],
): { from: string; to: string }[] {
  const known = new Set(nodes.map(node => node.id));
  const edges: { from: string; to: string }[] = [];

  for (const component of components) {
    const from = key(component.name);
    for (const target of component.targets) {
      const to = isInternalTarget(target, components)
        ? key(target.raw)
        : externalId(target);
      if (!known.has(to) || to === from) {
        continue;
      }
      if (edges.some(edge => edge.from === from && edge.to === to)) {
        continue;
      }
      edges.push({ from, to });
    }
  }

  return edges;
}

export function reduceTransitive(
  edges: { from: string; to: string }[],
): { from: string; to: string }[] {
  const outgoing = new Map<string, string[]>();
  for (const edge of edges) {
    const list = outgoing.get(edge.from);
    if (list) {
      list.push(edge.to);
    } else {
      outgoing.set(edge.from, [edge.to]);
    }
  }

  const reachesIndirectly = (from: string, to: string): boolean => {
    const seen = new Set<string>([from]);
    const stack = (outgoing.get(from) ?? []).filter(next => next !== to);

    while (stack.length > 0) {
      const current = stack.pop() as string;
      if (current === to) {
        return true;
      }
      if (seen.has(current)) {
        continue;
      }
      seen.add(current);
      stack.push(...(outgoing.get(current) ?? []));
    }

    return false;
  };

  return edges.filter(edge => !reachesIndirectly(edge.from, edge.to));
}

export function rankNodes(
  nodes: RawNode[],
  edges: { from: string; to: string }[],
): Map<string, number> {
  const incoming = new Map<string, string[]>();
  for (const node of nodes) {
    incoming.set(node.id, []);
  }
  for (const edge of edges) {
    incoming.get(edge.to)?.push(edge.from);
  }

  const ranks = new Map<string, number>();
  const visiting = new Set<string>();

  const rankOf = (id: string): number => {
    const cached = ranks.get(id);
    if (cached !== undefined) {
      return cached;
    }
    if (visiting.has(id)) {
      return 0;
    }

    visiting.add(id);
    const parents = incoming.get(id) ?? [];
    const rank = parents.reduce(
      (max, parent) => Math.max(max, rankOf(parent) + 1),
      0,
    );
    visiting.delete(id);
    ranks.set(id, rank);
    return rank;
  };

  for (const node of nodes) {
    rankOf(node.id);
  }

  return ranks;
}

const HIT_MARGIN = 6;

function hitsNode(
  segment: { x1: number; y1: number; x2: number; y2: number },
  node: C4LayoutNode,
): boolean {
  const left = node.x - HIT_MARGIN;
  const right = node.x + node.width + HIT_MARGIN;
  const top = node.y - HIT_MARGIN;
  const bottom = node.y + node.height + HIT_MARGIN;

  const minX = Math.min(segment.x1, segment.x2);
  const maxX = Math.max(segment.x1, segment.x2);
  const minY = Math.min(segment.y1, segment.y2);
  const maxY = Math.max(segment.y1, segment.y2);

  return minX <= right && maxX >= left && minY <= bottom && maxY >= top;
}

export function needsChannel(
  from: C4LayoutNode,
  to: C4LayoutNode,
  nodes: C4LayoutNode[] = [],
): boolean {
  if (to.rank <= from.rank) {
    return true;
  }

  const x1 = from.x + from.width / 2;
  const x2 = to.x + to.width / 2;
  const y1 = from.y + from.height;
  const y2 = to.y;
  const midY = y1 + (y2 - y1) / 2;
  const segments = [
    { x1, y1, x2: x1, y2: midY },
    { x1, y1: midY, x2, y2: midY },
    { x1: x2, y1: midY, x2, y2 },
  ];

  return nodes.some(
    node =>
      node.id !== from.id &&
      node.id !== to.id &&
      segments.some(segment => hitsNode(segment, node)),
  );
}

function edgePath(
  from: C4LayoutNode,
  to: C4LayoutNode,
  channelX: number,
): string {
  if (channelX === 0) {
    const x1 = Math.round(from.x + from.width / 2);
    const y1 = from.y + from.height;
    const x2 = Math.round(to.x + to.width / 2);
    const y2 = to.y;
    const midY = Math.round(y1 + (y2 - y1) / 2);
    if (x1 === x2) {
      return `M ${x1},${y1} L ${x2},${y2}`;
    }
    return `M ${x1},${y1} L ${x1},${midY} L ${x2},${midY} L ${x2},${y2}`;
  }

  const y1 = Math.round(from.y + from.height / 2);
  const y2 = Math.round(to.y + to.height / 2);
  const x1 = from.x + from.width;
  const x2 = to.x + to.width;
  return `M ${x1},${y1} L ${channelX},${y1} L ${channelX},${y2} L ${x2},${y2}`;
}

export function internalBounds(
  layout: C4Layout,
): { x: number; y: number; width: number; height: number } | undefined {
  const internal = layout.nodes.filter(node => node.type === 'internal');
  if (internal.length === 0) {
    return undefined;
  }

  const minX = Math.min(...internal.map(node => node.x));
  const minY = Math.min(...internal.map(node => node.y));
  const maxX = Math.max(...internal.map(node => node.x + node.width));
  const maxY = Math.max(...internal.map(node => node.y + node.height));

  return {
    x: minX - BOUNDARY_PADDING,
    y: minY - BOUNDARY_PADDING,
    width: maxX - minX + BOUNDARY_PADDING * 2,
    height: maxY - minY + BOUNDARY_PADDING * 2,
  };
}

export function layoutC4Components(
  components: C4ComponentDefinition[],
): C4Layout {
  if (components.length === 0) {
    return { nodes: [], edges: [], width: 0, height: 0 };
  }

  const raw = collectNodes(components);
  const rawEdges = reduceTransitive(collectEdges(components, raw));
  const ranks = rankNodes(raw, rawEdges);

  const lastInternalRank = raw
    .filter(node => node.type === 'internal')
    .reduce((max, node) => Math.max(max, ranks.get(node.id) ?? 0), 0);
  for (const node of raw) {
    if (node.type === 'external') {
      ranks.set(node.id, lastInternalRank + 1);
    }
  }

  const byRank = new Map<number, RawNode[]>();
  for (const node of raw) {
    const rank = ranks.get(node.id) ?? 0;
    const row = byRank.get(rank);
    if (row) {
      row.push(node);
    } else {
      byRank.set(rank, [node]);
    }
  }

  const sortedRanks = [...byRank.keys()].sort((a, b) => a - b);
  const rowWidths = sortedRanks.map(rank => {
    const row = byRank.get(rank) ?? [];
    return row.length * CARD_WIDTH + (row.length - 1) * GAP_X;
  });
  const contentWidth = Math.max(...rowWidths);

  const nodes: C4LayoutNode[] = [];
  let y = CANVAS_PADDING;

  sortedRanks.forEach((rank, index) => {
    const row = byRank.get(rank) ?? [];
    const rowWidth = rowWidths[index];
    const startX = CANVAS_PADDING + Math.round((contentWidth - rowWidth) / 2);
    let rowHeight = 0;

    row.forEach((node, column) => {
      const height = nodeHeight(node);
      rowHeight = Math.max(rowHeight, height);
      nodes.push({
        ...node,
        rank,
        x: startX + column * (CARD_WIDTH + GAP_X),
        y,
        width: CARD_WIDTH,
        height,
      });
    });

    y += rowHeight + GAP_Y;
  });

  const byId = new Map(nodes.map(node => [node.id, node]));
  const laneBase = CANVAS_PADDING + contentWidth + CHANNEL_WIDTH;
  let lanes = 0;

  const edges: C4LayoutEdge[] = rawEdges
    .map(edge => {
      const from = byId.get(edge.from);
      const to = byId.get(edge.to);
      if (!from || !to) {
        return undefined;
      }

      const channelX = needsChannel(from, to, nodes)
        ? laneBase + lanes++ * CHANNEL_LANE_WIDTH
        : 0;
      return {
        from: edge.from,
        to: edge.to,
        path: edgePath(from, to, channelX),
      };
    })
    .filter((edge): edge is C4LayoutEdge => Boolean(edge));

  return {
    nodes,
    edges,
    width:
      CANVAS_PADDING * 2 +
      contentWidth +
      (lanes > 0 ? CHANNEL_WIDTH + lanes * CHANNEL_LANE_WIDTH : 0),
    height: y - GAP_Y + CANVAS_PADDING,
  };
}
